import { useEffect, useState } from "react";
import { useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Grid3x3, Bath, Droplets, Package, ArrowLeft } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { SIZES, SURFACES, RATE_UNITS, TILE_TYPES, PIECE_BUILTINS } from "@/lib/format";
import MediaUpload from "@/components/MediaUpload";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const CAT_ICONS = { tiles: Grid3x3, sanitarywares: Bath, sinks: Droplets };
const CAT_DESC = {
  tiles: "Wall / floor tiles — sq.ft, box & piece pricing",
  sanitarywares: "Closets, basins, WCs — priced per piece",
  sinks: "Kitchen & utility sinks — priced per piece",
};

export default function AddProduct() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const editId = sp.get("edit");
  const { sub, access } = useOutletContext();
  const [cities, setCities] = useState([]);
  const [cats, setCats] = useState([]);
  const [opts, setOpts] = useState({ sizes: SIZES, surfaces: SURFACES, units: RATE_UNITS, localities: [] });
  const [form, setForm] = useState({ rate_unit: "", size: "", surface: "" });
  const [busy, setBusy] = useState(false);
  const [sizeCustom, setSizeCustom] = useState(false);
  const [surfaceCustom, setSurfaceCustom] = useState(false);
  const [unitCustom, setUnitCustom] = useState(false);
  const [localityCustom, setLocalityCustom] = useState(false);
  useEffect(() => { if (form.size && !opts.sizes.includes(form.size)) setSizeCustom(true); }, [form.size, opts.sizes]);
  useEffect(() => { if (form.surface && !opts.surfaces.includes(form.surface)) setSurfaceCustom(true); }, [form.surface, opts.surfaces]);
  useEffect(() => { if (form.locality && opts.localities.length && !opts.localities.includes(form.locality)) setLocalityCustom(true); }, [form.locality, opts.localities]);

  useEffect(() => {
    if (editId || !sub) return;
    if (access === "restricted") {
      toast.info("Choose a package to start listing your products");
      nav("/seller/subscription", { replace: true });
    } else if (access === "full" && (sub.slots?.available_slots ?? 0) <= 0) {
      toast.info("All your product slots are used. Buy an add-on package to list more");
      nav("/seller/subscription", { replace: true });
    }
  }, [editId, access, sub, nav]);

  useEffect(() => {
    api.get("/cities?active=true").then(({ data }) => setCities(data));
    api.get("/categories").then(({ data }) => setCats(data)).catch(() => {});
    if (editId) api.get("/products").then(({ data }) => { const p = data.find((x) => x.id === editId); if (p) setForm({ ...p, category: p.category || "tiles" }); });
  }, [editId]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (form.category) params.set("category", form.category);
    if (form.city) params.set("city", form.city);
    api.get(`/product-options?${params.toString()}`).then(({ data }) => setOpts(data)).catch(() => {});
  }, [form.city, form.category]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const cat = form.category || "tiles";
  const isTiles = cat === "tiles";
  const isPiece = PIECE_BUILTINS.includes(cat);
  const isGeneric = !isTiles && !isPiece;
  const catLabel = cats.find((c) => c.value === cat)?.label || cat;

  const submit = async (e) => {
    e.preventDefault();
    let req;
    if (isTiles) req = ["size", "surface", "clearance_price", "box_price", "catalogue_image", "stock_image", "stock_video", "city"];
    else if (isPiece) req = ["size", "surface", "pc_price", "catalogue_image", "stock_image", "stock_video", "city"];
    else req = ["size", "surface", "rate_unit", "rate_value", "catalogue_image", "stock_image", "stock_video", "city"];
    for (const r of req) if (!form[r]) {
      if (isTiles) toast.error("Fill all mandatory fields (incl. clearance & box price) and upload 2 images + 1 video");
      else if (isPiece) toast.error("Fill all mandatory fields (incl. price per piece) and upload 2 images + 1 video");
      else toast.error("Fill all mandatory fields (incl. unit & offer price) and upload 2 images + 1 video");
      return;
    }
    setBusy(true);
    try {
      if (form.locality && form.city && !opts.localities.includes(form.locality)) {
        await api.post("/localities", { city: form.city, name: form.locality }).catch(() => {});
      }
      const payload = {
        ...form,
        category: cat,
        actual_price: form.actual_price ? Number(form.actual_price) : null,
        clearance_price: isTiles ? Number(form.clearance_price) : null,
        box_price: isTiles ? Number(form.box_price) : null,
        pc_price: form.pc_price ? Number(form.pc_price) : null,
        rate_value: isGeneric ? Number(form.rate_value) : (form.rate_value ? Number(form.rate_value) : null),
        rate_unit: isGeneric ? form.rate_unit : (form.rate_unit || "Box"),
        stock_qty: Number(form.stock_qty) || 0,
      };
      if (!isTiles) { payload.tile_type = ""; payload.packing_per_box = ""; }
      if (editId) await api.put(`/products/${editId}`, payload);
      else await api.post("/products", payload);
      toast.success(editId ? "Product updated — approval will be done within 24 hours" : "Product submitted — approval will be done within 24 hours");
      nav("/seller/products");
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  const slots = sub?.slots;

  // Step 1 — pick a product line (new products only)
  if (!editId && !form.category) {
    return (
      <div className="space-y-5 max-w-3xl">
        <div>
          <h1 className="font-heading text-2xl font-extrabold">Add Product</h1>
          <p className="text-muted-foreground text-sm font-medium">Choose what you are listing to get the right form.</p>
        </div>
        <div className="grid sm:grid-cols-3 gap-4">
          {cats.map((c) => {
            const Icon = CAT_ICONS[c.value] || Package;
            return (
              <button
                key={c.value}
                data-testid={`pick-category-${c.value}`}
                onClick={() => set("category", c.value)}
                className="group text-left bg-card border rounded-2xl p-5 hover:border-primary hover:shadow-lg transition-all"
              >
                <div className="h-11 w-11 rounded-xl bg-primary/10 text-primary grid place-items-center group-hover:bg-primary group-hover:text-white transition-colors">
                  <Icon className="h-6 w-6" />
                </div>
                <div className="mt-3 font-heading font-extrabold text-lg">{c.label}</div>
                <div className="text-xs text-muted-foreground font-medium mt-1">{CAT_DESC[c.value] || "Choose unit & price while listing"}</div>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5 max-w-3xl">
      <div>
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="font-heading text-2xl font-extrabold">{editId ? "Edit Product" : "Add Product"}</h1>
          <span data-testid="selected-category-badge" className="text-xs font-bold px-2.5 py-1 rounded-full bg-primary/10 text-primary">{catLabel}</span>
          {!editId && (
            <button type="button" data-testid="change-category-btn" onClick={() => set("category", "")} className="text-xs font-semibold text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
              <ArrowLeft className="h-3 w-3" /> Change
            </button>
          )}
        </div>
        <p className="text-muted-foreground text-sm font-medium">
          Exactly 2 images + 1 real stock video required. {slots && !editId && <span className="font-bold text-primary">{slots.available_slots} slots available</span>}
        </p>
        <p data-testid="approval-notice" className="mt-2 text-xs font-semibold text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-1.5 inline-block">⏱ Approval will be done within 24 hours of upload.</p>
      </div>

      {access !== "full" && <div className="bg-rose-50 border border-rose-300 rounded-lg px-4 py-2 text-sm font-semibold text-rose-800">Adding/editing is disabled in your current subscription state.</div>}

      <form onSubmit={submit} className="space-y-5">
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <Label>Product Title</Label>
            <Input data-testid="product-title" value={form.title || ""} onChange={(e) => set("title", e.target.value)} placeholder={isTiles ? "e.g. Calacatta White Polished Vitrified" : `e.g. ${catLabel} — Glossy White`} />
          </div>
          <div>
            <Label>Size *</Label>
            <Select
              value={sizeCustom ? "__custom__" : (form.size || "")}
              onValueChange={(v) => { if (v === "__custom__") { setSizeCustom(true); set("size", ""); } else { setSizeCustom(false); set("size", v); } }}
            >
              <SelectTrigger data-testid="product-size"><SelectValue placeholder="Select size" /></SelectTrigger>
              <SelectContent>
                {opts.sizes.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                <SelectItem value="__custom__">➕ Add custom size…</SelectItem>
              </SelectContent>
            </Select>
            {sizeCustom && (
              <Input data-testid="product-size-custom" className="mt-2" value={form.size || ""} onChange={(e) => set("size", e.target.value)} placeholder={isTiles ? "e.g. 1200x2400mm" : "e.g. 560x360mm"} autoFocus />
            )}
          </div>
          <div>
            <Label>Surface *</Label>
            <Select
              value={surfaceCustom ? "__custom__" : (form.surface || "")}
              onValueChange={(v) => { if (v === "__custom__") { setSurfaceCustom(true); set("surface", ""); } else { setSurfaceCustom(false); set("surface", v); } }}
            >
              <SelectTrigger data-testid="product-surface"><SelectValue placeholder="Select surface" /></SelectTrigger>
              <SelectContent>
                {opts.surfaces.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                <SelectItem value="__custom__">➕ Add custom surface…</SelectItem>
              </SelectContent>
            </Select>
            {surfaceCustom && (
              <Input data-testid="product-surface-custom" className="mt-2" value={form.surface || ""} onChange={(e) => set("surface", e.target.value)} placeholder={isTiles ? "e.g. Sugar Finish" : "e.g. Ceramic White"} autoFocus />
            )}
          </div>

          {isTiles && (
            <>
              <div>
                <Label>Tile Type</Label>
                <Select value={form.tile_type || ""} onValueChange={(v) => set("tile_type", v)}>
                  <SelectTrigger data-testid="product-tile-type"><SelectValue placeholder="Wall or Floor" /></SelectTrigger>
                  <SelectContent>{TILE_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Packing per Box</Label>
                <Input data-testid="product-packing" value={form.packing_per_box || ""} onChange={(e) => set("packing_per_box", e.target.value)} placeholder="e.g. 4 pcs / box or 15.50 sq.ft / box" />
              </div>
            </>
          )}

          {isTiles && (
            <>
              <div>
                <Label>Actual Price (₹ / sq.ft)</Label>
                <Input data-testid="product-actual-price" type="number" value={form.actual_price || ""} onChange={(e) => set("actual_price", e.target.value)} placeholder="Original rate (optional)" />
              </div>
              <div>
                <Label>Clearance Price * (₹ / sq.ft)</Label>
                <Input data-testid="product-clearance-price" type="number" value={form.clearance_price || ""} onChange={(e) => set("clearance_price", e.target.value)} placeholder="Offer rate per sq.ft" />
              </div>
              <div>
                <Label>Price per Box * (₹)</Label>
                <Input data-testid="product-box-price" type="number" value={form.box_price || ""} onChange={(e) => set("box_price", e.target.value)} placeholder="e.g. 748" />
              </div>
              <div>
                <Label>Price per Piece (₹)</Label>
                <Input data-testid="product-pc-price" type="number" value={form.pc_price || ""} onChange={(e) => set("pc_price", e.target.value)} placeholder="Optional" />
              </div>
            </>
          )}

          {isPiece && (
            <>
              <div>
                <Label>Actual Price (₹ / piece)</Label>
                <Input data-testid="product-actual-price" type="number" value={form.actual_price || ""} onChange={(e) => set("actual_price", e.target.value)} placeholder="Original rate per piece (optional)" />
              </div>
              <div>
                <Label>Clearance Price per Piece * (₹)</Label>
                <Input data-testid="product-pc-price" type="number" value={form.pc_price || ""} onChange={(e) => set("pc_price", e.target.value)} placeholder="Offer rate per piece" />
              </div>
            </>
          )}

          {isGeneric && (
            <>
              <div>
                <Label>Rate Unit *</Label>
                <Select
                  value={unitCustom ? "__custom__" : (form.rate_unit || "")}
                  onValueChange={(v) => { if (v === "__custom__") { setUnitCustom(true); set("rate_unit", ""); } else { setUnitCustom(false); set("rate_unit", v); } }}
                >
                  <SelectTrigger data-testid="product-rate-unit"><SelectValue placeholder="e.g. Piece, Set, Sq.ft" /></SelectTrigger>
                  <SelectContent>
                    {opts.units.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                    <SelectItem value="__custom__">➕ Add custom unit…</SelectItem>
                  </SelectContent>
                </Select>
                {unitCustom && (
                  <Input data-testid="product-rate-unit-custom" className="mt-2" value={form.rate_unit || ""} onChange={(e) => set("rate_unit", e.target.value)} placeholder="e.g. Set, Litre, Nos" autoFocus />
                )}
              </div>
              <div>
                <Label>Actual Price (₹ / {(form.rate_unit || "unit").toLowerCase()})</Label>
                <Input data-testid="product-actual-price" type="number" value={form.actual_price || ""} onChange={(e) => set("actual_price", e.target.value)} placeholder="Original rate (optional)" />
              </div>
              <div>
                <Label>Offer Price * (₹ / {(form.rate_unit || "unit").toLowerCase()})</Label>
                <Input data-testid="product-rate-value" type="number" value={form.rate_value || ""} onChange={(e) => set("rate_value", e.target.value)} placeholder="Clearance rate" />
              </div>
            </>
          )}

          <div>
            <Label>{isTiles ? "Stock Available (Boxes)" : "Stock Available (Pieces)"}</Label>
            {editId ? (
              <>
                <Input data-testid="product-qty" type="number" value={form.stock_qty || 0} disabled className="bg-secondary/50" />
                <p className="text-[11px] text-muted-foreground font-medium mt-1">Stock is managed via <b>Reduce Inventory</b> in My Products.</p>
              </>
            ) : (
              <Input data-testid="product-qty" type="number" value={form.stock_qty || ""} onChange={(e) => set("stock_qty", e.target.value)} placeholder={isTiles ? "140" : "50"} />
            )}
          </div>
          <div>
            <Label>City *</Label>
            <Select value={form.city || ""} onValueChange={(v) => set("city", v)}>
              <SelectTrigger data-testid="product-city"><SelectValue placeholder="Select city" /></SelectTrigger>
              <SelectContent>{cities.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Stock Location (Locality)</Label>
            <Select
              value={localityCustom ? "__custom__" : (form.locality || "")}
              onValueChange={(v) => { if (v === "__custom__") { setLocalityCustom(true); set("locality", ""); } else { setLocalityCustom(false); set("locality", v); } }}
            >
              <SelectTrigger data-testid="product-locality"><SelectValue placeholder="Select locality" /></SelectTrigger>
              <SelectContent>
                {opts.localities.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                <SelectItem value="__custom__">➕ Add custom location…</SelectItem>
              </SelectContent>
            </Select>
            {localityCustom && (
              <Input data-testid="product-locality-custom" className="mt-2" value={form.locality || ""} onChange={(e) => set("locality", e.target.value)} placeholder="e.g. Ring Road, Rajkot" autoFocus />
            )}
          </div>
          <div className="sm:col-span-2 flex items-center justify-between gap-3 rounded-xl border p-3 bg-secondary/30">
            <div>
              <Label className="text-sm">Show this listing to PAN India</Label>
              <p className="text-xs text-muted-foreground font-medium">When on, buyers in every city see this listing regardless of the city they selected.</p>
            </div>
            <Switch data-testid="product-pan-india" checked={!!form.pan_india} onCheckedChange={(v) => set("pan_india", v)} />
          </div>
          <div className="sm:col-span-2">
            <Label>Description</Label>
            <Textarea data-testid="product-description" value={form.description || ""} onChange={(e) => set("description", e.target.value)} rows={3} placeholder="Batch/shade details, thickness, reason for clearance, etc." />
          </div>
          <div className="sm:col-span-2">
            <Label>Search Keywords <span className="text-muted-foreground font-normal">(help buyers find this)</span></Label>
            <Input data-testid="product-keywords" value={form.keywords || ""} onChange={(e) => set("keywords", e.target.value)} placeholder="comma separated e.g. marble look, bathroom, glossy white" />
          </div>
        </div>

        <div className="grid sm:grid-cols-3 gap-4">
          <MediaUpload testId="upload-catalogue" label="Catalogue Image" hint="Professional reference image" kind="image" value={form.catalogue_image} onChange={(v) => set("catalogue_image", v)} />
          <MediaUpload testId="upload-stock-image" label="Original Stock Image" hint="Actual photo of physical stock" kind="image" value={form.stock_image} onChange={(v) => set("stock_image", v)} />
          <MediaUpload testId="upload-stock-video" label="Real Stock Video" hint="Video of actual stock" kind="video" value={form.stock_video} onChange={(v) => set("stock_video", v)} />
        </div>

        <Button data-testid="submit-product" type="submit" disabled={busy || access !== "full" || (slots && slots.available_slots <= 0)} className="font-bold h-11 px-8">
          {busy ? "Saving…" : editId ? "Update Product" : "Submit for Approval"}
        </Button>
      </form>
    </div>
  );
}
